import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { 
  HH_SIZE_PATTERN, 
  ID_NUMBER_PATTERN, 
  NUMBER_DECIMAL_PATTERN, 
  SOCIAL_SECURITY_PATTERN, 
  ZIP_CODE_PATTERN,
  TEN_DIGIT_NUMBER_PATTERN
} from './validation-patterns';

/**
 * Validator for US ZIP codes (5 digits only).
 * Accepts format: 12345
 * 
 * @returns ValidatorFn that validates ZIP code format
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   zipCode: ['', zipCodeValidator()]
 * });
 * ```
 */
export function zipCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const zipPattern = ZIP_CODE_PATTERN;
    const isValid = zipPattern.test(control.value);

    return isValid ? null : { invalidZipCode: { value: control.value } };
  };
}

/**
 * Validator for US Social Security Numbers.
 * Accepts formats: 123-45-6789, 123456789
 * 
 * @returns ValidatorFn that validates SSN format
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   ssn: ['', ssnValidator()]
 * });
 * ```
 */
export function ssnValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const ssnPattern = SOCIAL_SECURITY_PATTERN;
    const isValid = ssnPattern.test(control.value);

    return isValid ? null : { invalidSSN: { value: control.value } };
  };
}

/**
 * Validator for the last 4 digits of a Social Security Number.
 * Pads the value with a fixed prefix so validation reuses {@link ssnValidator}.
 * 
 * @returns ValidatorFn that validates SSN last 4 digits format
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   ssnLast4: ['', ssnLast4Validator()]
 * });
 * ```
 */
export function ssnLast4Validator(): ValidatorFn {
  const validateFullSsn = ssnValidator();

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const last4 = String(control.value).trim();
    const errors = validateFullSsn({ value: `12345${last4}` } as AbstractControl);

    return errors ? { invalidSSN: { value: control.value } } : null;
  };
}

/**
 * Validator for URLs.
 * Validates http and https URLs. Requires valid protocol.
 * 
 * @returns ValidatorFn that validates URL format
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   website: ['', urlValidator()]
 * });
 * ```
 */
export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    try {
      const url = new URL(control.value);
      return (url.protocol === 'http:' || url.protocol === 'https:') 
        ? null 
        : { invalidUrl: { value: control.value } };
    } catch {
      return { invalidUrl: { value: control.value } };
    }
  };
}

/**
 * Validator that checks if a numeric value is within a specified range.
 * 
 * @param min - Minimum allowed value (inclusive)
 * @param max - Maximum allowed value (inclusive)
 * @returns ValidatorFn that validates numeric range
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   age: ['', [Validators.required, numericRangeValidator(0, 120)]]
 * });
 * ```
 */
export function numericRangeValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value && control.value !== 0) {
      return null;
    }

    const value = Number(control.value);
    
    if (Number.isNaN(value)) {
      return { notANumber: true };
    }

    if (value < min) {
      return { belowMinimum: { min, actual: value } };
    }

    if (value > max) {
      return { aboveMaximum: { max, actual: value } };
    }

    return null;
  };
}

/**
 * Validator that ensures a string contains only alphanumeric characters.
 * Optionally allows spaces and specific special characters.
 * 
 * @param allowSpaces - Whether to allow spaces (default: false)
 * @param allowedSpecialChars - String of special characters to allow (default: '')
 * @returns ValidatorFn that validates alphanumeric content
 * 
 * @example
 * ```typescript
 * // Only letters and numbers
 * this.form = this.fb.group({
 *   username: ['', alphanumericValidator()]
 * });
 * 
 * // Allow spaces and hyphens
 * this.form = this.fb.group({
 *   displayName: ['', alphanumericValidator(true, '-')]
 * });
 * ```
 */
export function alphanumericValidator(allowSpaces = false, allowedSpecialChars = ''): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const specialChars = allowedSpecialChars.replaceAll(/[-[\]{}()*+?.,\\^$|#]/g, String.raw`\$&`);
    const spacePattern = allowSpaces ? String.raw`\s` : '';
    const pattern = new RegExp(`^[a-zA-Z0-9${spacePattern}${specialChars}]+$`);

    const isValid = pattern.test(control.value);

    return isValid ? null : { invalidAlphanumeric: { value: control.value } };
  };
}

/**
 * Validator that requires at least one checkbox to be checked in a FormArray.
 * 
 * @returns ValidatorFn that validates at least one item is selected
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   interests: this.fb.array([], atLeastOneCheckedValidator())
 * });
 * ```
 */
export function atLeastOneCheckedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || !Array.isArray(control.value)) {
      return { noneChecked: true };
    }

    const hasChecked = control.value.some((value: unknown) => value === true || value === 'true');
    
    return hasChecked ? null : { noneChecked: true };
  };
}


/**
 * Validator function for Federal Employer Identification Number (FEIN) input.
 *
 * Validates that the control value contains exactly 9 numeric digits (`^\d{9}$`).
 * If the control value is empty or not provided, validation is skipped and `null` is returned.
 *
 * @returns A `ValidatorFn` that returns:
 * - `null` when the value is empty or matches a valid 9-digit FEIN.
 * - `ValidationErrors` with the `invalidFEIN` key and the original value when invalid.
 */
export function feinValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const feinPattern = /^\d{9}$/;
    const isValid = feinPattern.test(control.value);

    return isValid ? null : { invalidFEIN: { value: control.value } };
  };
}

/**
 * Validator for PowerSchool/UID values.
 * Accepts numbers only.
 *
 * @returns ValidatorFn that validates numeric-only UID format
 */
export function numberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const uidValue = String(control.value);
    const uidPattern = ID_NUMBER_PATTERN;
    const isValid = uidPattern.test(uidValue);

    return isValid ? null : { invalidNumber: { value: control.value } };
  };
}

/**
 * Validator that accepts only exactly 10 numeric digits.
 *
 * @returns ValidatorFn that validates a strict 10-digit number format
 */
export function tenDigitNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = String(control.value).trim();
    const tenDigitPattern = TEN_DIGIT_NUMBER_PATTERN;
    const isValid = tenDigitPattern.test(value);

    return isValid ? null : { invalidTenDigitNumber: { value: control.value } };
  };
}

/**
 * Validator for household size.
 * Accepts numeric values with up to 2 digits (0-99).
 *
 * @returns ValidatorFn that validates household size format
 */
export function householdSizeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value && control.value !== 0) {
      return null;
    }

    const householdSizeValue = String(control.value).trim();
    const householdSizePattern = HH_SIZE_PATTERN;
    const isValid = householdSizePattern.test(householdSizeValue);

    return isValid ? null : { invalidHHSize: { value: control.value } };
  };
}

/**
 * Validator that allows whole numbers or decimal numbers.
 * Accepts formats: 123, 123.45
 *
 * @returns ValidatorFn that validates numeric/decimal format
 */
export function numericOrDecimalValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value && control.value !== 0) {
      return null;
    }

    const value = String(control.value).trim();
    const numberOrDecimalPattern = NUMBER_DECIMAL_PATTERN;
    const isValid = numberOrDecimalPattern.test(value);

    return isValid ? null : { invalidNumberOrDecimal: { value: control.value } };
  };
}

/**
 * Validator for address/city fields.
 * Rejects values that contain only whitespace.
 *
 * @returns ValidatorFn that validates non-whitespace text input
 */
export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == null || control.value === '') {
      return null;
    }

    const value = String(control.value);
    return value.trim().length === 0
      ? { whitespaceOnly: { value: control.value } }
      : null;
  };
}